// Shopify supplies versioned URLs so browsers receive updated configuration and
// chat fixes instead of retaining unversioned CDN modules for a year.
const loader = document.querySelector('script[data-support-widget-loader]');
const [{ SUPPORT_CONFIG }, { openChat }] = await Promise.all([
  import(loader?.dataset.configUrl || './support-config.js'),
  import(loader?.dataset.chatUrl || './support-chat.js'),
]);

// A light-DOM web component, matching the theme's existing quantum-benefits component.
export class SupportWidget extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    const on = (target, event, callback, options = {}) => target.addEventListener(event, callback, { ...options, signal: this.abort.signal });
    this.launcher = this.querySelector('[data-support-launcher]');
    this.panel = this.querySelector('[data-support-panel]');
    this.closeButton = this.querySelector('[data-support-close]');
    this.chatButton = this.querySelector('[data-support-chat]');
    this.status = this.querySelector('[data-support-status]');
    this.opened = false;
    this.chatActive = false;
    this.requestId = 0;
    this.mobileViewport = window.matchMedia('(max-width: 820px)');
    on(this.mobileViewport, 'change', () => this.updateVisibility());
    const phone = this.querySelector('[data-support-phone]');
    phone.textContent = SUPPORT_CONFIG.phoneDisplay;
    phone.href = `tel:${SUPPORT_CONFIG.phoneHref}`;
    this.querySelector('[data-support-call]').href = phone.href;
    this.querySelector('[data-support-weekdays]').textContent = `Mon–Fri: ${SUPPORT_CONFIG.hours.weekdays}`;
    this.querySelector('[data-support-weekends]').textContent = `Sat–Sun: ${SUPPORT_CONFIG.hours.weekends}`;
    const email = this.querySelector('[data-support-email]');
    email.textContent = SUPPORT_CONFIG.email;
    email.href = `mailto:${SUPPORT_CONFIG.email}?subject=${encodeURIComponent(SUPPORT_CONFIG.emailSubject)}`;
    this.querySelector('[data-support-mail]').href = email.href;
    on(this.launcher, 'click', () => this.opened ? this.close() : this.open());
    on(this.closeButton, 'click', () => this.close());
    on(document, 'click', (event) => {
      if (this.opened && !this.contains(event.target)) this.close();
    });
    on(document, 'keydown', (event) => {
      if (this.opened && event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      }
    }, { capture: true });
    // Non-modal dialog: Tab can leave naturally. Close on click (after the browser's
    // pointer focus change) so outside dismissal reliably returns launcher focus.
    on(this.chatButton, 'click', () => this.startChat());
    on(document, 'qpc:chat-closed', () => {
      if (!this.chatActive) return;
      this.chatActive = false;
      this.updateVisibility();
      this.launcher.focus({ preventScroll: true });
    });
    // Hide behind mobile purchasing controls; never reposition the launcher.
    this.observer = new MutationObserver(() => this.updateVisibility());
    this.observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    this.updateVisibility();
  }

  open() {
    this.opened = true;
    this.panel.hidden = false;
    this.launcher.setAttribute('aria-expanded', 'true');
    this.launcher.setAttribute('aria-label', 'Close customer support');
    this.updateVisibility();
    this.closeButton.focus({ preventScroll: true });
  }

  close(restoreFocus = true) {
    this.opened = false;
    this.requestId += 1; // A late tawk response must not reopen a dismissed panel.
    this.panel.hidden = true;
    this.launcher.setAttribute('aria-expanded', 'false');
    this.launcher.setAttribute('aria-label', 'Open customer support');
    this.updateVisibility();
    if (restoreFocus && !this.hidden) this.launcher.focus({ preventScroll: true });
  }

  async startChat() {
    if (this.chatPending) return;
    this.chatPending = true;
    const request = ++this.requestId;
    this.chatButton.setAttribute('aria-disabled', 'true');
    this.chatButton.setAttribute('aria-busy', 'true');
    this.status.textContent = 'Connecting to live chat…';
    const result = await openChat(SUPPORT_CONFIG, () => this.opened && this.isConnected && request === this.requestId);
    this.chatPending = false;
    this.chatButton.removeAttribute('aria-disabled');
    this.chatButton.removeAttribute('aria-busy');
    if (request !== this.requestId) { this.status.textContent = ''; return; }
    if (result === 'opened') {
      this.chatActive = true;
      this.close(false);
      this.status.textContent = '';
    } else {
      this.status.textContent = result === 'unconfigured'
        ? 'Live chat is being configured.'
        : 'Live chat is temporarily unavailable. Please call or email us.';
      this.status.scrollIntoView({ block: 'nearest', behavior: 'instant' });
      this.chatButton.focus({ preventScroll: true });
    }
  }

  updateVisibility() {
    const blocked = document.body.matches('.is-cart-open, .is-search-open, .is-mobile-menu-open, .is-product-lightbox-open, .is-qpc-consent-preferences-open');
    const mobileCartVisible = this.mobileViewport.matches
      && document.body.classList.contains('is-product-sticky-bar-visible');
    this.hidden = blocked || this.chatActive || mobileCartVisible;
    if (this.hidden && this.opened) {
      this.opened = false;
      this.requestId += 1;
      this.panel.hidden = true;
      this.launcher.setAttribute('aria-expanded', 'false');
      this.launcher.setAttribute('aria-label', 'Open customer support');
    }
  }

  disconnectedCallback() {
    this.requestId += 1;
    this.abort?.abort();
    this.abort = null;
    this.observer?.disconnect();
  }
}

if (!customElements.get('quantum-support')) customElements.define('quantum-support', SupportWidget);
